// src/components/brand/no-brands-screen.tsx
import { useState } from "react";
import { Building2, RefreshCcw, LogOut, Loader2, Mail } from "lucide-react";
import { UpmodeLogo } from "../login/upmode-logo";

interface NoBrandsScreenProps {
  /** Re-fetch /brands/me — admin may have just assigned a brand. */
  onRetry: () => Promise<void> | void;
  onLogout: () => void;
  fullName?: string | null;
}

/**
 * Full-bleed screen shown when a brand-scoped user has no brands
 * assigned. They cannot reach brand-search, so this is their landing
 * state until an admin assigns a brand.
 */
export function NoBrandsScreen({ onRetry, onLogout, fullName }: NoBrandsScreenProps) {
  const [checking, setChecking] = useState(false);

  const handleRetry = async () => {
    setChecking(true);
    try {
      await onRetry();
    } finally {
      setChecking(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "var(--background)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "clamp(16px, 4vh, 32px) clamp(16px, 4vw, 28px)",
        position: "relative",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `
            radial-gradient(circle at 20% 20%, var(--spectral-violet-5) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, var(--signal-blue-5) 0%, transparent 50%)
          `,
          opacity: 0.5,
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          width: "100%",
          maxWidth: 520,
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "clamp(16px, 3vh, 26px)",
        }}
      >
        <div style={{ display: "inline-flex" }}>
          <UpmodeLogo />
        </div>

        <div
          style={{
            width: "100%",
            backgroundColor: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: 16,
            padding: "clamp(28px, 4vh, 44px) clamp(24px, 4vw, 40px)",
            boxShadow: "var(--elevation-lg)",
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background:
                "linear-gradient(90deg, var(--spectral-violet), var(--signal-blue), var(--alpha-teal))",
              borderRadius: "16px 16px 0 0",
              opacity: 0.8,
            }}
          />

          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 16,
              margin: "0 auto 20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "var(--signal-blue-10)",
              border: "1px solid var(--signal-blue-25)",
            }}
          >
            <Building2 style={{ width: 30, height: 30, color: "var(--signal-blue)" }} />
          </div>

          <h1
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: "var(--text-2xl)",
              fontWeight: "var(--font-weight-semibold)",
              color: "var(--foreground)",
              margin: 0,
              lineHeight: "var(--leading-tight)",
              letterSpacing: "-0.01em",
            }}
          >
            No brands associated
          </h1>

          <p
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: "var(--text-base)",
              color: "var(--muted-foreground)",
              lineHeight: "var(--leading-relaxed)",
              margin: "12px auto 0",
              maxWidth: 400,
            }}
          >
            {fullName ? `${fullName}, your` : "Your"} account doesn't have any
            brands assigned yet. Please contact the Upmode admin team to assign
            your brand.
          </p>

          <div
            style={{
              marginTop: 22,
              padding: "12px 16px",
              borderRadius: "var(--radius)",
              backgroundColor: "var(--muted)",
              border: "1px solid var(--border)",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontFamily: "var(--font-sans)",
              fontSize: "var(--text-sm)",
              color: "var(--muted-foreground)",
            }}
          >
            <Mail style={{ width: 16, height: 16, color: "var(--signal-blue)" }} />
            Reach out to your Upmode administrator
          </div>

          <div
            style={{
              marginTop: 26,
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <button
              onClick={handleRetry}
              disabled={checking}
              style={{
                width: "100%",
                height: 48,
                fontFamily: "var(--font-sans)",
                fontSize: "var(--text-base)",
                fontWeight: "var(--font-weight-semibold)",
                background: checking ? "var(--muted)" : "var(--alpha-teal)",
                color: "white",
                border: "none",
                borderRadius: "var(--radius)",
                cursor: checking ? "default" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              {checking ? (
                <>
                  <Loader2 className="animate-spin" style={{ width: 18, height: 18 }} />
                  Checking…
                </>
              ) : (
                <>
                  <RefreshCcw style={{ width: 18, height: 18 }} />
                  Check again
                </>
              )}
            </button>

            <button
              onClick={onLogout}
              style={{
                width: "100%",
                height: 44,
                fontFamily: "var(--font-sans)",
                fontSize: "var(--text-sm)",
                fontWeight: "var(--font-weight-medium)",
                background: "transparent",
                color: "var(--muted-foreground)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              <LogOut style={{ width: 16, height: 16 }} />
              Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}